<template>
  <div class="w-full sm:w-auto">
    <label
      :for="disabled ? 'disabled' + question.id : question.id"
      class="block mb-2 w-full text-sm font-semibold leading-6 text-gray-800 sm:w-auto">
      {{ question.title }}
    </label>
    <div class="relative">
      <div
        class="flex absolute inset-y-0 top-0 items-center pointer-events-none end-0 pe-3.5"></div>
      <input
        v-if="question.mwDataType.name === MWDataTypes.TIME"
        type="time"
        :id="disabled ? 'disabled' + question.id : question.id"
        v-model="data"
        :disabled="disabled"
        @blur="v$.model.$touch"
        class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-theme-500 focus:border-theme-500"
        required
        :class="{
          'border-red-500 ring-red-500': v$.model.$error && v$.model.$dirty,
        }" />

      <input
        v-if="question.mwDataType.name === MWDataTypes.DATE"
        type="date"
        :id="disabled ? 'disabled' + question.id : question.id"
        v-model="data"
        :disabled="disabled"
        @blur="v$.model.$touch"
        class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-theme-500 focus:border-theme-500"
        :class="{
          'border-red-500 ring-red-500': v$.model.$error && v$.model.$dirty,
        }"
        required />

      <input
        v-if="question.mwDataType.name === MWDataTypes.DATETIME"
        type="datetime-local"
        :id="disabled ? 'disabled' + question.id : question.id"
        v-model="data"
        :disabled="disabled"
        @blur="v$.model.$touch"
        class="block px-3 py-3.5 w-full text-sm text-gray-600 rounded-md border-0 ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-theme-400 focus:ring-inset sm:text-sm sm:leading-6 focus-visible:outline-none"
        :class="{
          'border-red-500 ring-red-500': v$.model.$error && v$.model.$dirty,
        }"
        required />
    </div>
    <p v-if="v$.model.$error && v$.model.$dirty" class="mt-1 text-xs text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type { QstQuestionDetails } from '@/types/Questionnaires';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed, defineModel } from 'vue';
import { useI18n } from 'vue-i18n';
import { MWDataTypes } from '@/utils/constants';

const props = withDefaults(
  defineProps<{
    question: QstQuestionDetails;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const data = defineModel<string>({
  default: '',
});

const { t: $t } = useI18n({ useScope: 'global' });
const rules = computed(() => {
  const record: Record<string, any> = {};

  if (!props.question.allowNull) {
    record.required = helpers.withMessage($t('messages.errors.requiredField'), required);
  }

  return { model: record };
});

const v$ = useVuelidate(rules, { model: data }, { $lazy: true });

const firstError = computed(() => {
  const errs = v$.value.model.$errors;
  return errs.length ? errs[0].$message : $t('messages.errors.invalidValue');
});
</script>
